import React, { FC, useState, useRef, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { StorageProvider } from 'providers/storage'
import ReactMapGL, {
  ScaleControl,
  NavigationControl,
  Source,
  Layer,
  PointerEvent,
  FlyToInterpolator,
  WebMercatorViewport,
  HTMLOverlay,
} from 'react-map-gl'
import { isEmpty } from 'ramda'
import { Editor } from './components/Editor'
import { Menu } from 'features/shared/Menu'
import { LayerGroup } from 'models/layer'
import { MapTools } from './components/MapTools'
import { Wizards } from '../Wizards'
import { MeasureTool } from './components/MeasureTool'
import { AreaTools } from 'models/editor'
import { Infowindow } from './components/Infowindow'
import { easeCubic } from 'd3-ease'
import { debounce } from 'utils/debounce'
import { Project, Area } from 'models/project'
import { ProjectArea } from './components/ProjectArea'
import { CompareArea } from './components/CompareArea'
import { useProjectContext } from 'context/project-context'
import { useMapEditorContext } from 'context/map-editor-context'
import {
  buildMapLayers,
  buildTilesUrl,
  getFilterExpression,
  buildMapLayersIcons,
  transformRequest,
} from './map-utils'
import { getBoundsFromGeometry } from 'utils/coordinates'
import { Bounds } from 'models/map'
import { Pin } from './components/Pin'
import { Legends } from './components/Legends'
import { on } from 'utils/customEvent'
import { RefreshLayer, Events, FlyToBounds } from 'constants/events'
import styles from './Map.module.scss'
import { SeePonderaReport } from '../Wizards/components/shared/SeePonderaReport'
import { VisualizationTools } from './components/VisualizationTools'

// https://webpack.js.org/guides/dependency-management/#require-context
const requireIcons = require.context('assets/map-icons', false, /.png$/)

type MapProps = {
  layers: LayerGroup[]
  project?: Project
  area?: Area
}

type Viewport = {
  latitude: number
  longitude: number
  zoom: number
  transitionDuration?: number | 'auto' | undefined
  transitionInterpolator?: FlyToInterpolator
  transitionEasing?: (normalizedTime: number) => number
}
const DEFAULT_COORDINATES = [-99.14, 19.4]
const MAX_ZOOM = 20
const DEFAULT_ZOOM = 12

var FlagPinPoint = false

export const Map: FC<MapProps> = ({ layers = [], project, area }) => {
  //alert(longitud)
  const mapRef = useRef<any>()
  const location: any = useLocation()
  const { areaId, projectId } = useParams()
  const [{ activeLayers, hiddenLayers, filters, search }, projectDispatch] = useProjectContext()
  const [{ areaTool, wizard }] = useMapEditorContext()

  const layersToShow = buildMapLayers(layers, activeLayers)
  const layerIcons = buildMapLayersIcons(layers)
  const [mapStyle, setMapStyle] = useState<string>(
    'mapbox://styles/danielpruebas/ckn9f0gtn051p17nqbeal08ku',
  )

  const coordinates = location.state?.search
    ? location.state?.search.coordinates
    : DEFAULT_COORDINATES

  const [infowindow, setInfowindow] = useState<any>()
  const [viewport, setViewport] = useState<Viewport>({
    latitude: coordinates[1],
    longitude: coordinates[0],
    zoom: DEFAULT_ZOOM,
  })

  const getBoundedViewport = (bounds: Bounds) => {
    const { _width: width, _height: height } = mapRef.current
    const { zoom, latitude, longitude } = new WebMercatorViewport({ width, height }).fitBounds(
      bounds,
    )
    return { latitude, longitude, zoom: Math.min(MAX_ZOOM, zoom) }
  }

  // Persist the search when coming from  the dashboard
  useEffect(() => {
    projectDispatch.setSearch(location.state?.search || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Center the project once it loads
  useEffect(() => {
    if (!project) {
      //condition to know if there's a search priority
      if (location.state?.search) {
        projectDispatch.setSearch(location.state?.search || null)
      } else {
        let uem = StorageProvider.get('email')
        fetch('https://mapserver.descifra.global/v1/getCentroidCoverage/' + uem)
          .then((response) => response.json())
          .then((data) => setViewport({ longitude: data[0].x, latitude: data[0].y, zoom: 10 }))
      }
    } else {
      const projectBounds = getBoundsFromGeometry(project?.geometry)
      const newViewport = getBoundedViewport(projectBounds)
      setViewport(newViewport)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Center on the bounds if they change
  useEffect(() => {
    return on<FlyToBounds>(Events.FlyToBounds, (event) => {
      const newViewport = getBoundedViewport(event.detail.bounds)
      flyTo({ ...newViewport, transitionDuration: 2000 })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresh the given source layer
  // https://github.com/mapbox/mapbox-gl-js/issues/2633
  useEffect(() => {
    return on<RefreshLayer>(Events.RefreshLayer, (event) => {
      const sourceLayer = event.detail.sourceLayer
      const map = mapRef.current.getMap()
      const cache = map.style.sourceCaches[sourceLayer]

      cache.clearTiles()
      cache.update(map.transform)

      map.triggerRepaint()
    })
  }, [])

  const flyTo = (newViewport: Partial<Viewport>) => {
    setViewport({
      ...viewport,
      transitionDuration: 5000,
      transitionInterpolator: new FlyToInterpolator(),
      transitionEasing: easeCubic,
      ...newViewport,
    })
  }

  useEffect(
    () => {
      const bounds = mapRef.current.getMap().getBounds()

      projectDispatch.setBoundingBox({
        northWestLat: bounds._ne.lat,
        northWestLon: bounds._sw.lng,
        southEastLat: bounds._sw.lat,
        southEastLon: bounds._ne.lng,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapRef.current],
  )

  const updateBoundingBox = debounce(() => {
    if (!mapRef.current) return
    if (isEmpty(layersToShow)) return

    const bounds = mapRef.current.getMap().getBounds()

    projectDispatch.setBoundingBox({
      northWestLat: bounds._ne.lat,
      northWestLon: bounds._sw.lng,
      southEastLat: bounds._sw.lat,
      southEastLon: bounds._ne.lng,
    })
  }, 300)

  const [activePinpoint, setActivePinpoint] = useState<boolean>(false)

  const onPinPointClick = () => {
    if (FlagPinPoint === true) {
      FlagPinPoint = false
    } else {
      FlagPinPoint = true
    }

    setActivePinpoint(!activePinpoint)

    /*if (markers.length > 0) {
      for (var i = markers.length - 1; i >= 0; i--) {
        markers[i].remove()
      }
      setMarkers([])
    }*/
  }

  const onMapClick = ({ target, features, lngLat }: PointerEvent) => {
    // We didn't find out a way to cancel mouse events that
    // weren't directly on the map so we decided to check
    // the element type
    if (target.tagName !== 'svg') return
    if (wizard) return
    if (features.length === 0) return

    const id = features[0]?.id
    const layerId = features[0]?.layer.id
    var editable2 = null

    layers.forEach(myFunction2)

    function myFunction2(item: any) {
      if (String(item.name) === 'Mis datasets') {
        item.layers.forEach(myFunction)
      }
    }

    function myFunction(item: any) {
      if (String(item.id) === String(layerId)) {
        editable2 = item.editable
      }
    }

    if (id) {
      setInfowindow({ id, layerId, lngLat, editable2 })
    }
  }

  function RenderIcons() {
    const map = mapRef.current.getMap()
    layerIcons.forEach((icon: string) => {
      map.loadImage(icon, (error: any, image: any) => {
        if (error) return
        map.addImage(icon, image, { sdf: true })
      })
    })

    requireIcons.keys().forEach((fileName) => {
      const iconExport = requireIcons(fileName)
      // fileName is './diamond.png' and we want 'diamond'
      const iconName = fileName.replace(/^\.\//, '').replace(/\.\w+$/, '')

      map.loadImage(iconExport, (error: any, image: any) => {
        if (error) return
        map.addImage(iconName, image, { sdf: true })
      })
    })
  }

  const handleMapStyle = (mapURL: string) => {
    RenderIcons()
    setMapStyle(mapURL)
    RenderIcons()
    RenderIcons()
  }

  const onLightMapClick = () => {
    handleMapStyle('mapbox://styles/danielpruebas/ckn9f0gtn051p17nqbeal08ku')
  }

  const onDarkMapClick = () => {
    handleMapStyle('mapbox://styles/danielpruebas/ckn9ehs1v04o617tblbi871it')
  }

  const onSatelliteMapClick = () => {
    handleMapStyle('mapbox://styles/danielpruebas/ckn9f5u4n058y17n07gyivrs2')
  }

  const onStreetMapClick = () => {
    handleMapStyle('mapbox://styles/danielpruebas/cklilstby02dd17qmjm1hyves')
  }

  function CargaUbProyecto() {
    const map = mapRef.current.getMap()
    console.log(map)
    if (project?.centroid['coordinates']) {
      console.log('map')
    }
  }

  const loadingLayers = false
  var flagCorte = localStorage.getItem('clipped')
  let tiles = null

  if (flagCorte === '' || flagCorte === null) {
    flagCorte = 'true'
    localStorage.setItem('clipped', flagCorte)
  }

  function saveClipped() {
    var statusclipped = localStorage.getItem('clipped')

    if (statusclipped === 'true') {
      statusclipped = 'false'
    } else {
      statusclipped = 'true'
    }

    var sourceS = localStorage.getItem('source')
    var projectIdS = localStorage.getItem('projectId')
    var areaIdS = localStorage.getItem('areaId')
    //var flagCorteS = localStorage.getItem("clipped")

    localStorage.setItem('clipped', statusclipped)

    buildTilesUrl(sourceS!, projectIdS!, areaIdS!, statusclipped!)

    window.location.reload()
  }

  const [flagRType, setflagRType] = React.useState(true)
  const [flagRPondera, setflagRPondera] = React.useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var url =
      'https://mapserver.descifra.global/v1/getQueryRes/public.project?columns=*&filter=id=' +
      projectId // eslint-disable-next-line react-hooks/exhaustive-deps
    if (window.location.href.includes('projects')) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetch(url) // eslint-disable-next-line react-hooks/exhaustive-deps
        .then((response) => response.json()) // eslint-disable-next-line react-hooks/exhaustive-deps
        .then(
          (data) => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (data[0]['reporttype'] === 2 || data[0]['reporttype'] === '2') {
              // eslint-disable-next-line react-hooks/exhaustive-deps
              setflagRType(false) // eslint-disable-next-line react-hooks/exhaustive-deps
              setflagRPondera(true) // eslint-disable-next-line react-hooks/exhaustive-deps
            } // eslint-disable-next-line react-hooks/exhaustive-deps
          }, // eslint-disable-next-line react-hooks/exhaustive-deps
        ) // eslint-disable-next-line react-hooks/exhaustive-deps
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <div className={styles.map} onLoad={CargaUbProyecto}>
      {loadingLayers === false && (
        <ReactMapGL
          width="100%"
          height="100%"
          {...viewport}
          onViewportChange={({ width, height, ...other }) => {
            setViewport(other)
            updateBoundingBox()
          }}
          mapStyle={mapStyle}
          mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_KEY}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          attributionControl={false}
          dragRotate={false}
          maxZoom={MAX_ZOOM}
          onClick={onMapClick}
          // The type has to be a string
          interactiveLayerIds={activeLayers?.map((a) => a.toString()) as any}
          ref={mapRef}
          transformRequest={transformRequest}
          onLoad={(map) => {
            layerIcons.forEach((icon: string) => {
              map.target.loadImage(icon, (error: any, image: any) => {
                if (error) return
                map.target.addImage(icon, image, { sdf: true })
              })
            })

            requireIcons.keys().forEach((fileName) => {
              const iconExport = requireIcons(fileName)
              // fileName is './diamond.png' and we want 'diamond'
              const iconName = fileName.replace(/^\.\//, '').replace(/\.\w+$/, '')

              map.target.loadImage(iconExport, (error: any, image: any) => {
                if (error) return
                map.target.addImage(iconName, image, { sdf: true })
              })
            })
          }}
        >
          <div style={{ position: 'absolute', top: 16, right: 16 }}>
            <Menu hasShadow />
          </div>

          <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
            <VisualizationTools
              onLightMapClick={onLightMapClick}
              onDarkMapClick={onDarkMapClick}
              onSatelliteMapClick={onSatelliteMapClick}
              onStreetMapClick={onStreetMapClick}
              saveClipped={saveClipped}
              project={project}
              projectId={projectId}
            />
            <NavigationControl showCompass={false} />
          </div>

          <div style={{ position: 'absolute', bottom: 16, right: 60 }}>
            <ScaleControl maxWidth={100} unit="metric" />
          </div>

          <MapTools
            isPinpointActive={activePinpoint ? true : undefined}
            onPinPointClick={onPinPointClick}
            flagRType={flagRType}
          />

          <div
            style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}
          >
            <Wizards />
            {areaTool === AreaTools.MEASURE && <MeasureTool />}
          </div>

          {infowindow && (
            <Infowindow
              layerId={infowindow.layerId}
              infowindowId={infowindow.id}
              lngLat={infowindow.lngLat}
              editable2={infowindow.editable2}
              onClose={() => setInfowindow(null)}
            />
          )}

          <HTMLOverlay
            redraw={() => <Legends />}
            style={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              width: '248px',
              height: 'auto',
              top: 'auto',
            }}
            captureScroll
            captureDrag
            captureClick
            captureDoubleClick
          />

          {search && <Pin longitude={search.coordinates[0]} latitude={search.coordinates[1]} />}

          <Editor />

          {Object.entries(layersToShow).map(([source, layers]) => {
            tiles = buildTilesUrl(source, projectId, areaId, String(flagCorte))
            localStorage.setItem('source', source)
            localStorage.setItem('projectId', projectId)
            localStorage.setItem('areaId', areaId)

            return (
              <Source key={source} id={source} type="vector" tiles={[tiles]}>
                {layers.map((layer) => {
                  const id = `${layer.id}-${layer.viz['source-layer']}`
                  const visibility = hiddenLayers.includes(layer.id) ? 'none' : 'visible'

                  return (
                    <Layer
                      key={id}
                      // The type has to be a string
                      id={layer.id.toString()}
                      type={layer.viz.type}
                      {...{ 'source-layer': layer.viz['source-layer'] }}
                      paint={layer.viz.paint}
                      minzoom={layer.viz.minzoom ? layer.viz.minzoom : 0}
                      layout={{ ...layer.viz.layout, visibility }}
                      filter={getFilterExpression(layer, filters)}
                    />
                  )
                })}
              </Source>
            )
          })}
          {flagRPondera && (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <SeePonderaReport />
            </div>
          )}
          <ProjectArea project={project} centro={false} />
          <ProjectArea project={project} centro={true} />
          <CompareArea area={area} centro={false} />
          <CompareArea area={area} centro={true} />
        </ReactMapGL>
      )}
    </div>
  )
}
